import React, { useMemo } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import type { FormData, OptionItem } from "../FormRenderer/types";
import { useAsyncOptions } from "../FormRenderer/useAsyncOptions";
import {
  ensureValuesInOptions,
  getBaseOptionsForField,
  getValuesToCover,
} from "../FormRenderer/formFieldUtils";
import type { TaskTableField } from "../../types/manualAssessmentWorkflow";
import {
  normalizeTaskTableAsyncConfig,
  taskTableFieldHasAsyncOptions,
} from "../../utils/taskTableAsyncConfig";
import { HOME_LIST_TABLE_CELL_FIELD_SX, HOME_LIST_TABLE_TOKENS } from "../../styles/homeListTableTokens";

export type TaskTableAsyncSelectProps = Readonly<{
  field: TaskTableField;
  value: unknown;
  disabled?: boolean;
  formData?: FormData;
  onChange: (value: unknown) => void;
  error?: boolean;
  helperText?: string;
}>;

type SelectOption = {
  label: string;
  value: string;
};

const filterSelectOptions = createFilterOptions<SelectOption>({
  stringify: (option) => option.label,
});

function toStaticOptions(field: TaskTableField): OptionItem[] {
  return (field.options ?? []).map((option) => ({
    label: option.label,
    value: typeof option.value === "boolean" ? String(option.value) : option.value,
  }));
}

function getSelectEmptyLabel(field: TaskTableField): string {
  return typeof field.placeholder === "string" && field.placeholder.trim()
    ? field.placeholder
    : "Select";
}

function getFieldAriaLabel(field: TaskTableField, fallback: string): string {
  return typeof field.label === "string" && field.label.trim()
    ? field.label.trim()
    : fallback;
}

function toSelectOptions(options: OptionItem[]): SelectOption[] {
  return options.map((option) => ({
    label: option.label,
    value: String(option.value),
  }));
}

export function TaskTableAsyncSelect({
  field,
  value,
  disabled = false,
  formData,
  onChange,
  error = false,
  helperText,
}: TaskTableAsyncSelectProps) {
  const asyncConfig = normalizeTaskTableAsyncConfig(field);
  const multiple = field.multiple === true;
  const { options: asyncOptions, loading } = useAsyncOptions(asyncConfig, formData);

  const selectOptions = useMemo((): OptionItem[] => {
    const staticOptions = toStaticOptions(field);
    const hasAsync = taskTableFieldHasAsyncOptions(field);
    const useAsyncAsBase = hasAsync && asyncOptions.length > 0;
    const base = getBaseOptionsForField(useAsyncAsBase, staticOptions, asyncOptions);
    return ensureValuesInOptions(base, getValuesToCover(multiple, value));
  }, [asyncOptions, field, multiple, value]);

  const options = useMemo(() => toSelectOptions(selectOptions), [selectOptions]);

  const autocompleteValue = useMemo((): SelectOption | SelectOption[] | null => {
    if (multiple) {
      const ids = Array.isArray(value)
        ? value.map((item) => String(item))
        : value == null || value === ""
          ? []
          : [String(value)];
      return ids
        .map((id) => options.find((option) => option.value === id))
        .filter((option): option is SelectOption => option != null);
    }
    if (value == null || value === "") return null;
    return options.find((option) => option.value === String(value)) ?? null;
  }, [multiple, options, value]);

  const emptyLabel = getSelectEmptyLabel(field);
  // Wait for async options before rendering the input. Otherwise a stored
  // value (user id) is padded into options via ensureValuesInOptions and briefly
  // displays as the raw id until labels resolve (e.g. re-toggling assessment required).
  const isLoading = Boolean(asyncConfig?.url) && loading;

  if (isLoading) {
    return (
      <Box
        role="status"
        aria-label="Loading options"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: HOME_LIST_TABLE_TOKENS.cellControlMinHeight,
          width: "100%",
        }}
      >
        <CircularProgress size={18} />
      </Box>
    );
  }

  return (
    <Autocomplete
      fullWidth
      size="small"
      multiple={multiple}
      disabled={disabled}
      disableCloseOnSelect={multiple}
      options={options}
      value={autocompleteValue}
      filterOptions={filterSelectOptions}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      onChange={(_event, next) => {
        if (multiple) {
          const selected = Array.isArray(next) ? next : [];
          onChange(selected.map((option) => option.value));
          return;
        }
        const selected = next && !Array.isArray(next) ? next : null;
        onChange(selected ? selected.value : null);
      }}
      renderTags={(selected) => {
        if (selected.length === 0) return null;
        const label =
          selected.length === 1
            ? selected[0]?.label
            : `${selected.length} selected`;
        return <span>{label}</span>;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={emptyLabel}
          error={error}
          helperText={helperText}
          sx={HOME_LIST_TABLE_CELL_FIELD_SX}
          inputProps={{
            ...params.inputProps,
            "aria-label": getFieldAriaLabel(field, emptyLabel),
          }}
        />
      )}
      sx={{
        "& .MuiOutlinedInput-root": {
          paddingTop: 0,
          paddingBottom: 0,
        },
        "& .MuiAutocomplete-input": {
          minWidth: "4ch",
        },
      }}
    />
  );
}

export default TaskTableAsyncSelect;
